import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Pagination,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';

import dayjs from 'dayjs';
import { fetchClients } from '@src/api/actions/clients';
import {
  approveTaskProposal,
  cancelTaskSend,
  bulkCompleteTasks,
  bulkDeleteTasks,
  createAssignmentRule,
  createTask,
  deleteAssignmentRule,
  deleteTask,
  fetchTasksSourceCounts,
  fetchTasksSummary,
  listAssignmentRules,
  listAssignmentUsers,
  listAssignees,
  listTasks,
  fetchTasksSummaryAudio,
  rejectTaskProposal,
  snoozeTask,
  updateAssignmentRule,
  updateAssignmentUser,
  updateTask,
} from '@src/api/tasks';
import { useMessage } from '@src/context/common';

import TaskFilters from './components/TaskFilters';
import TaskColumn from './components/TaskColumn';
import TaskDialog from './components/TaskDialog';
import StatusDialog from './components/StatusDialog';
import TaskTable from './components/TaskTable';
import TaskAssignmentSettingsDialog from './components/TaskAssignmentSettingsDialog';

const DEFAULT_STATUS_COLUMNS = [
  { value: 'agent_proposed', label: 'Agent Proposed' },
  { value: 'backlog', label: 'Backlog' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'review', label: 'Review' },
  { value: 'completed', label: 'Completed' },
];

const STATUS_STORAGE_KEY = 'leadguruspro.tasks.status-columns';
const DEFAULT_SORT_CONFIG = {
  field: 'created_at',
  direction: 'asc',
};

function formatStatusLabel(value) {
  if (!value) {
    return '';
  }
  return value
    .toString()
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function loadStoredStatuses() {
  if (typeof window === 'undefined') {
    return DEFAULT_STATUS_COLUMNS;
  }
  try {
    const raw = window.localStorage.getItem(STATUS_STORAGE_KEY);
    if (!raw) {
      return DEFAULT_STATUS_COLUMNS;
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return DEFAULT_STATUS_COLUMNS;
    }
    const map = new Map();
    parsed.forEach((item) => {
      if (item?.value) {
        map.set(item.value, { value: item.value, label: item.label || formatStatusLabel(item.value) });
      }
    });
    DEFAULT_STATUS_COLUMNS.forEach((status) => {
      if (!map.has(status.value)) {
        map.set(status.value, status);
      }
    });
    return DEFAULT_STATUS_COLUMNS.map((status) => map.get(status.value)).concat(
      Array.from(map.values()).filter((item) => !DEFAULT_STATUS_COLUMNS.some((def) => def.value === item.value)),
    );
  } catch (error) {
    console.warn('Failed to parse stored task statuses', error);
    return DEFAULT_STATUS_COLUMNS;
  }
}

function persistStatuses(statuses) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(STATUS_STORAGE_KEY, JSON.stringify(statuses));
  } catch (error) {
    console.warn('Failed to persist task statuses', error);
  }
}

function buildOrdering(sortConfig = DEFAULT_SORT_CONFIG) {
  const sortableField = sortConfig.field === 'due_date' ? 'due_date' : 'created_at';
  const directionPrefix = sortConfig.direction === 'desc' ? '-' : '';

  return `is_completed,${directionPrefix}${sortableField}`;
}

function buildQueryParams(filters, sortConfig = DEFAULT_SORT_CONFIG, page = 1) {
  return {
    search: filters.search || undefined,
    client: filters.client || undefined,
    assignee: filters.assignee || undefined,
    priority: filters.priority !== 'all' ? filters.priority : undefined,
    status: filters.status !== 'all' ? filters.status : undefined,
    source: filters.source !== 'all' ? filters.source : undefined,
    source_work_decision:
      filters.sourceWorkDecision !== 'all' ? filters.sourceWorkDecision : undefined,
    source_router_category:
      filters.sourceRouterCategory !== 'all' ? filters.sourceRouterCategory : undefined,
    created_after: filters.createdAfter || undefined,
    created_before: filters.createdBefore || undefined,
    due_after: filters.dueAfter || undefined,
    due_before: filters.dueBefore || undefined,
    is_completed: filters.hideCompleted ? 'false' : undefined,
    ordering: buildOrdering(sortConfig),
    parent_only: 'true',
    page,
  };
}

const PAGE_SIZE = 50;

function mergeStatuses(current, tasks) {
  const existing = new Map(current.map((item) => [item.value, item]));
  let changed = false;

  tasks.forEach((task) => {
    if (task.status && !existing.has(task.status)) {
      existing.set(task.status, { value: task.status, label: formatStatusLabel(task.status) });
      changed = true;
    }
  });

  if (!changed) {
    return current;
  }

  const ordered = [];
  DEFAULT_STATUS_COLUMNS.forEach((status) => {
    if (existing.has(status.value)) {
      ordered.push(existing.get(status.value));
      existing.delete(status.value);
    }
  });
  existing.forEach((value) => {
    ordered.push(value);
  });
  return ordered;
}

function TaskBoardPage() {
  const message = useMessage();

  const [viewMode, setViewMode] = useState('table');
  const [filters, setFilters] = useState({
    search: '',
    client: null,
    assignee: null,
    status: 'all',
    priority: 'all',
    source: 'all',
    sourceWorkDecision: 'all',
    sourceRouterCategory: 'all',
    createdAfter: null,
    createdBefore: null,
    dueAfter: null,
    dueBefore: null,
    hideCompleted: true,
  });
  const [statusColumns, setStatusColumns] = useState(() => loadStoredStatuses());
  const [tasks, setTasks] = useState([]);
  const [sortConfig, setSortConfig] = useState(DEFAULT_SORT_CONFIG);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [sourceCounts, setSourceCounts] = useState({});
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  // Tracks an in-flight delayed email send so we can show a cancel snackbar.
  // Shape: { taskId, sendAt: ISO, taskTitle, secondsLeft }
  const [pendingSend, setPendingSend] = useState(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const audioRef = useRef(null);
  const audioUrlRef = useRef(null);

  const [clients, setClients] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [filtersLoading, setFiltersLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [assignmentUsers, setAssignmentUsers] = useState([]);
  const [assignmentRules, setAssignmentRules] = useState([]);
  const [savingAssignmentUserId, setSavingAssignmentUserId] = useState(null);
  const [savingAssignmentRuleId, setSavingAssignmentRuleId] = useState(null);
  const [deletingAssignmentRuleId, setDeletingAssignmentRuleId] = useState(null);

  const sortTasks = useCallback(
    (items, config = sortConfig) => {
      const sortableField = config.field === 'due_date' ? 'due_date' : 'created_at';
      const directionMultiplier = config.direction === 'desc' ? -1 : 1;

      return [...items].sort((a, b) => {
        const valueA = a[sortableField];
        const valueB = b[sortableField];

        if (!valueA && !valueB) {
          return 0;
        }
        if (!valueA) {
          return 1;
        }
        if (!valueB) {
          return -1;
        }

        const dateA = dayjs(valueA);
        const dateB = dayjs(valueB);

        if (dateA.isValid() && dateB.isValid()) {
          const diff = dateA.diff(dateB);
          if (diff === 0) {
            return 0;
          }
          return diff > 0 ? directionMultiplier : -directionMultiplier;
        }

        return directionMultiplier * String(valueA).localeCompare(String(valueB));
      });
    },
    [sortConfig],
  );

  const normalizeTask = useCallback(
    (task, config = sortConfig) => ({
      ...task,
      subtasks: sortTasks(task.subtasks || [], config),
    }),
    [sortTasks, sortConfig],
  );

  useEffect(() => {
    persistStatuses(statusColumns);
  }, [statusColumns]);

  const statusOptions = useMemo(
    () => statusColumns.map((status) => ({ value: status.value, label: status.label })),
    [statusColumns],
  );

  const tasksByStatus = useMemo(() => {
    const map = new Map(statusColumns.map((status) => [status.value, []]));
    tasks.forEach((task) => {
      const key = task.status || 'backlog';
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(task);
    });
    return map;
  }, [tasks, statusColumns]);

  const loadFiltersData = useCallback(() => {
    setFiltersLoading(true);
    Promise.all([fetchClients(), listAssignees()])
      .then(([clientsResponse, assigneesResponse]) => {
        const clientData = clientsResponse.data?.results || clientsResponse.data || [];
        const assigneeData = assigneesResponse.data?.results || assigneesResponse.data || [];

        setClients(
          clientData
            .map((client) => ({ id: client.id, name: client.name }))
            .sort((a, b) => a.name.localeCompare(b.name)),
        );
        setAssignees(
          assigneeData
            .map((assignee) => ({
              id: assignee.id,
              full_name: assignee.full_name,
              email: assignee.email,
            }))
            .sort((a, b) => (a.full_name || a.email || '').localeCompare(b.full_name || b.email || '')),
        );
      })
      .catch((err) => {
        console.error('Failed to load filter data', err);
        message.error('Unable to load clients or assignees.');
      })
      .finally(() => setFiltersLoading(false));
  }, [message]);

  useEffect(() => {
    loadFiltersData();
  }, [loadFiltersData]);

  const loadTasks = useCallback(() => {
    setLoading(true);
    setError('');
    const params = buildQueryParams(filters, sortConfig, page);
    listTasks(params)
      .then((response) => {
        const data = response.data?.results || response.data || [];
        const count = response.data?.count ?? data.length;
        const normalized = sortTasks(data, sortConfig).map((task) => normalizeTask(task, sortConfig));
        setTasks(normalized);
        setTotalCount(count);
        const flattened = data.flatMap((task) => [task, ...(task.subtasks || [])]);
        setStatusColumns((prev) => mergeStatuses(prev, flattened));
      })
      .catch((err) => {
        console.error('Failed to fetch tasks', err);
        setError('Unable to load tasks. Please try again.');
      })
      .finally(() => setLoading(false));
  }, [filters, sortConfig, sortTasks, page]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const loadSourceCounts = useCallback(() => {
    fetchTasksSourceCounts()
      .then((response) => setSourceCounts(response.data || {}))
      .catch((err) => console.error('Failed to fetch source counts', err));
  }, []);

  const loadSummary = useCallback((refresh = false) => {
    setSummaryLoading(true);
    fetchTasksSummary(refresh ? { refresh: 'true' } : {})
      .then((response) => setSummary(response.data || null))
      .catch((err) => console.error('Failed to fetch AI summary', err))
      .finally(() => setSummaryLoading(false));
  }, []);

  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setAudioPlaying(false);
  }, []);

  const handlePlayAudio = useCallback(() => {
    if (audioPlaying) {
      stopAudio();
      return;
    }
    // If we have a cached blob URL, just resume
    if (audioRef.current && audioRef.current.src) {
      audioRef.current.play().then(() => setAudioPlaying(true)).catch((err) => {
        console.error('Audio play failed', err);
        message.error('Unable to play audio.');
      });
      return;
    }
    setAudioLoading(true);
    fetchTasksSummaryAudio()
      .then((response) => {
        const blob = response.data;
        const url = URL.createObjectURL(blob);
        if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = url;
        if (!audioRef.current) {
          audioRef.current = new Audio();
          audioRef.current.addEventListener('ended', () => setAudioPlaying(false));
          audioRef.current.addEventListener('pause', () => {
            if (audioRef.current.currentTime > 0 && !audioRef.current.ended) {
              // user paused — keep audioPlaying false too
              setAudioPlaying(false);
            }
          });
        }
        audioRef.current.src = url;
        return audioRef.current.play();
      })
      .then(() => setAudioPlaying(true))
      .catch((err) => {
        console.error('Failed to fetch/play summary audio', err);
        const detail = err?.response?.status === 503
          ? 'Text-to-speech is not configured on the server.'
          : 'Unable to load audio briefing.';
        message.error(detail);
      })
      .finally(() => setAudioLoading(false));
  }, [audioPlaying, stopAudio, message]);

  // Stop audio + revoke blob URL on unmount
  useEffect(() => () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    if (audioUrlRef.current) URL.revokeObjectURL(audioUrlRef.current);
  }, []);

  // If summary content changes (refresh), invalidate cached audio so the
  // next play fetches a fresh narration matching the new text.
  useEffect(() => {
    if (audioRef.current && audioRef.current.src) {
      audioRef.current.pause();
      audioRef.current.src = '';
      setAudioPlaying(false);
    }
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }
  }, [summary?.generated_at]);

  useEffect(() => {
    loadSourceCounts();
    loadSummary();
  }, [loadSourceCounts, loadSummary]);

  // Filter/sort changes should reset to page 1
  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
  }, [filters, sortConfig]);

  const handleFiltersChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApproveProposal = (task, payload = {}) =>
    approveTaskProposal(task.id, payload)
      .then((response) => {
        const updated = response.data?.task || response.data;
        const result = response.data?.result || {};
        setTasks((prev) => prev.filter((t) => t.id !== task.id));

        // Detect a scheduled email send (any action with send_scheduled=true).
        const scheduledSend = (result.actions || [])
          .map((a) => a && a.result)
          .find((r) => r && r.send_scheduled);

        if (scheduledSend) {
          const windowSec = scheduledSend.cancel_window_seconds || 10;
          setPendingSend({
            taskId: task.id,
            sendAt: scheduledSend.send_at,
            taskTitle: task.title,
            secondsLeft: windowSec,
          });
        } else {
          message.success('Proposal approved & executed.');
        }
        loadSourceCounts();
        return updated;
      })
      .catch((err) => {
        console.error('Failed to approve proposal', err);
        const detail = err?.response?.data?.error || err?.response?.data?.detail || 'Unable to execute the proposal.';
        message.error(detail);
      });

  // Tick down the pendingSend countdown once a second; auto-dismiss at 0.
  useEffect(() => {
    if (!pendingSend) return undefined;
    if (pendingSend.secondsLeft <= 0) {
      const t = setTimeout(() => setPendingSend(null), 400);
      return () => clearTimeout(t);
    }
    const interval = setInterval(() => {
      setPendingSend((prev) => (prev ? { ...prev, secondsLeft: Math.max(0, prev.secondsLeft - 1) } : prev));
    }, 1000);
    return () => clearInterval(interval);
  }, [pendingSend]);

  const handleCancelPendingSend = () => {
    if (!pendingSend) return;
    const taskId = pendingSend.taskId;
    setPendingSend(null);
    cancelTaskSend(taskId)
      .then(() => {
        message.info('Email send cancelled. Draft is saved on the inbox thread.');
        loadTasks();
      })
      .catch((err) => {
        console.error('Failed to cancel pending send', err);
        message.error('Unable to cancel the send — it may have already gone out.');
      });
  };

  const handleRejectProposal = (task, reason = '') =>
    rejectTaskProposal(task.id, reason)
      .then((response) => {
        setTasks((prev) => prev.filter((t) => t.id !== task.id));
        message.success('Proposal rejected.');
        loadSourceCounts();
        return response.data;
      })
      .catch((err) => {
        console.error('Failed to reject proposal', err);
        message.error('Unable to reject the proposal.');
      });

  const handleSnooze = (task, until) => {
    snoozeTask(task.id, until)
      .then((response) => {
        const updated = response.data;
        // If snoozed into the future, drop it from the current page list.
        if (updated.snoozed_until && dayjs(updated.snoozed_until).isAfter(dayjs())) {
          setTasks((prev) => prev.filter((t) => t.id !== updated.id));
          message.success(`Snoozed until ${dayjs(updated.snoozed_until).format('MMM D, h:mma')}`);
        } else {
          upsertTaskInState(updated);
          message.success('Task woken up.');
        }
      })
      .catch((err) => {
        console.error('Failed to snooze', err);
        message.error('Unable to snooze the task.');
      });
  };

  const handleBulkDelete = () => {
    if (!selectedIds.size) return;
    if (!window.confirm(`Delete ${selectedIds.size} task${selectedIds.size === 1 ? '' : 's'}? This cannot be undone.`)) {
      return;
    }
    const ids = Array.from(selectedIds);
    bulkDeleteTasks(ids)
      .then((response) => {
        const deleted = response.data?.deleted ?? ids.length;
        message.success(`Deleted ${deleted} task${deleted === 1 ? '' : 's'}.`);
        setSelectedIds(new Set());
        loadTasks();
      })
      .catch((err) => {
        console.error('Failed to bulk delete', err);
        message.error('Unable to delete the selected tasks.');
      })
      .finally(loadSourceCounts);
  };

  const handleBulkComplete = () => {
    if (!selectedIds.size) return;
    const ids = Array.from(selectedIds);
    bulkCompleteTasks(ids)
      .then((response) => {
        const completed = response.data?.completed ?? ids.length;
        message.success(`Completed ${completed} task${completed === 1 ? '' : 's'}.`);
        if (filters.hideCompleted) {
          setTasks((prev) => prev.filter((t) => !selectedIds.has(t.id)));
        } else {
          loadTasks();
        }
        setSelectedIds(new Set());
      })
      .catch((err) => {
        console.error('Failed to bulk complete', err);
        message.error('Unable to complete the selected tasks.');
      })
      .finally(loadSourceCounts);
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      client: null,
      assignee: null,
      status: 'all',
      priority: 'all',
      source: 'all',
      sourceWorkDecision: 'all',
      sourceRouterCategory: 'all',
      createdAfter: null,
      createdBefore: null,
      dueAfter: null,
      dueBefore: null,
      hideCompleted: true,
    });
  };

  const openCreateDialog = () => {
    setEditingTask(null);
    setDialogOpen(true);
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingTask(null);
  };

  const handleStatusDialogClose = () => {
    setStatusDialogOpen(false);
  };

  const loadAssignmentSettings = useCallback(() => {
    return Promise.all([listAssignmentUsers(), listAssignmentRules()])
      .then(([usersResponse, rulesResponse]) => {
        const userData = usersResponse.data?.results || usersResponse.data || [];
        const ruleData = rulesResponse.data?.results || rulesResponse.data || [];
        setAssignmentUsers(userData);
        setAssignmentRules(ruleData);
      })
      .catch((err) => {
        console.error('Failed to load assignment settings', err);
        message.error('Unable to load inbox assignment settings.');
      });
  }, [message]);

  const openAssignmentDialog = () => {
    setAssignmentDialogOpen(true);
    loadAssignmentSettings();
  };

  const handleAssignmentDialogClose = () => {
    setAssignmentDialogOpen(false);
  };

  const upsertTaskInState = (updatedTask) => {
    setTasks((prev) => {
      if (updatedTask.parent) {
        return prev.map((task) => {
          if (task.id !== updatedTask.parent) {
            return task;
          }
          const normalizedChild = normalizeTask(updatedTask, sortConfig);
          const existingSubtasks = task.subtasks || [];
          const hasSubtask = existingSubtasks.some((subtask) => subtask.id === normalizedChild.id);
          const nextSubtasks = hasSubtask
            ? existingSubtasks.map((subtask) => (subtask.id === normalizedChild.id ? normalizedChild : subtask))
            : sortTasks([...existingSubtasks, normalizedChild], sortConfig);
          return { ...task, subtasks: nextSubtasks };
        });
      }
      const normalizedParent = normalizeTask(updatedTask, sortConfig);
      const exists = prev.some((task) => task.id === normalizedParent.id);
      const next = exists
        ? prev.map((task) => (task.id === normalizedParent.id ? normalizedParent : task))
        : [normalizedParent, ...prev];
      return sortTasks(next, sortConfig);
    });
    setStatusColumns((prev) => mergeStatuses(prev, [updatedTask]));
  };

  const handleCreateOrUpdateTask = (payload) => {
    const request = editingTask
      ? updateTask(editingTask.id, {
          title: payload.title,
          client: payload.clientId,
          assignee: payload.assigneeId,
          due_date: payload.dueDate,
          priority: payload.priority,
          status: payload.status,
          notes: payload.notes,
          description: payload.description,
          is_completed: payload.isCompleted,
        })
      : createTask({
          title: payload.title,
          client: payload.clientId,
          assignee: payload.assigneeId,
          due_date: payload.dueDate,
          priority: payload.priority,
          status: payload.status,
          notes: payload.notes,
          description: payload.description,
          is_completed: payload.isCompleted,
        });

    request
      .then((response) => {
        const updated = response.data;
        upsertTaskInState(updated);
        message.success(editingTask ? 'Task updated.' : 'Task created.');
        handleDialogClose();
      })
      .catch((err) => {
        console.error('Failed to save task', err);
        message.error('Unable to save the task.');
      });
  };

  const handleDeleteTask = (task) => {
    if (!window.confirm(`Delete "${task.title}"? This cannot be undone.`)) {
      return;
    }
    deleteTask(task.id)
      .then(() => {
        setTasks((prev) => {
          if (task.parent) {
            return prev.map((parentTask) => {
              if (parentTask.id !== task.parent) {
                return parentTask;
              }
              return {
                ...parentTask,
                subtasks: (parentTask.subtasks || []).filter((subtask) => subtask.id !== task.id),
              };
            });
          }
          return prev.filter((item) => item.id !== task.id);
        });
        message.success('Task deleted.');
      })
      .catch((err) => {
        console.error('Failed to delete task', err);
        message.error('Unable to delete the task.');
      });
  };

  const handleStatusChange = (task, status) => {
    updateTask(task.id, { status, is_completed: status === 'completed' ? true : task.is_completed })
      .then((response) => {
        upsertTaskInState(response.data);
        message.success('Task updated.');
      })
      .catch((err) => {
        console.error('Failed to update task status', err);
        message.error('Unable to update status.');
      });
  };

  const handleToggleComplete = (task, isCompleted) => {
    const nextStatus = isCompleted ? 'completed' : task.status === 'completed' ? 'backlog' : task.status;
    updateTask(task.id, { is_completed: isCompleted, status: nextStatus })
      .then((response) => {
        const updated = response.data;
        // When the task transitions to done and "hide completed" is on, drop
        // it from the current view (same behavior as snooze).
        if (updated.is_completed && filters.hideCompleted) {
          setTasks((prev) => prev.filter((t) => t.id !== updated.id));
          message.success('Marked done.');
        } else {
          upsertTaskInState(updated);
        }
        loadSourceCounts();
      })
      .catch((err) => {
        console.error('Failed to toggle task completion', err);
        message.error('Unable to update completion.');
      });
  };

  const handleAddStatus = ({ value, label }) => {
    setStatusColumns((prev) => {
      if (prev.some((item) => item.value === value)) {
        return prev;
      }
      const withoutCompleted = prev.filter((item) => item.value !== 'completed');
      const completedColumn = prev.find((item) => item.value === 'completed');
      const next = [...withoutCompleted, { value, label: label || formatStatusLabel(value) }];
      if (completedColumn) {
        next.push(completedColumn);
      }
      return next;
    });
    setStatusDialogOpen(false);
    message.success('Column added.');
  };

  const handleRefresh = () => {
    loadTasks();
  };

  const handleSaveAssignmentUser = (userId, payload) => {
    setSavingAssignmentUserId(userId);
    updateAssignmentUser(userId, payload)
      .then((response) => {
        const updated = response.data;
        setAssignmentUsers((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
        message.success('Assignment user updated.');
      })
      .catch((err) => {
        console.error('Failed to update assignment user', err);
        message.error('Unable to update assignment user.');
      })
      .finally(() => setSavingAssignmentUserId(null));
  };

  const handleCreateAssignmentRule = (payload) => {
    setSavingAssignmentRuleId(0);
    createAssignmentRule(payload)
      .then((response) => {
        const created = response.data;
        setAssignmentRules((prev) => [...prev, created].sort((a, b) => a.priority - b.priority || a.id - b.id));
        message.success('Assignment rule created.');
      })
      .catch((err) => {
        console.error('Failed to create assignment rule', err);
        message.error('Unable to create assignment rule.');
      })
      .finally(() => setSavingAssignmentRuleId(null));
  };

  const handleUpdateAssignmentRule = (ruleId, payload) => {
    setSavingAssignmentRuleId(ruleId);
    updateAssignmentRule(ruleId, payload)
      .then((response) => {
        const updated = response.data;
        setAssignmentRules((prev) =>
          prev
            .map((item) => (item.id === updated.id ? updated : item))
            .sort((a, b) => a.priority - b.priority || a.id - b.id),
        );
        message.success('Assignment rule updated.');
      })
      .catch((err) => {
        console.error('Failed to update assignment rule', err);
        message.error('Unable to update assignment rule.');
      })
      .finally(() => setSavingAssignmentRuleId(null));
  };

  const handleDeleteAssignmentRule = (ruleId) => {
    if (!window.confirm('Delete this assignment rule?')) {
      return;
    }
    setDeletingAssignmentRuleId(ruleId);
    deleteAssignmentRule(ruleId)
      .then(() => {
        setAssignmentRules((prev) => prev.filter((item) => item.id !== ruleId));
        message.success('Assignment rule deleted.');
      })
      .catch((err) => {
        console.error('Failed to delete assignment rule', err);
        message.error('Unable to delete assignment rule.');
      })
      .finally(() => setDeletingAssignmentRuleId(null));
  };

  const handleSortChange = (field, direction) => {
    setSortConfig((prev) => {
      if (prev.field === field && prev.direction === direction) {
        return prev;
      }
      return { field, direction };
    });
  };

  const boardContent = (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        overflowX: 'auto',
        pb: 1,
        pt: 1,
      }}
    >
      {statusColumns.map((status) => (
        <TaskColumn
          key={status.value}
          status={status}
          tasks={tasksByStatus.get(status.value) || []}
          statusOptions={statusOptions}
          onEdit={handleEditTask}
          onStatusChange={handleStatusChange}
          onToggleComplete={handleToggleComplete}
          onDelete={handleDeleteTask}
        />
      ))}
    </Box>
  );

  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const tableContent = (
    <Stack spacing={2}>
      <TaskTable
        tasks={tasks}
        loading={loading}
        sortConfig={sortConfig}
        selectedIds={selectedIds}
        onEdit={handleEditTask}
        onToggleComplete={handleToggleComplete}
        onDelete={handleDeleteTask}
        onSortChange={handleSortChange}
        onSelectionChange={setSelectedIds}
        onBulkDelete={handleBulkDelete}
        onBulkComplete={handleBulkComplete}
        onSnooze={handleSnooze}
        onApproveProposal={handleApproveProposal}
        onRejectProposal={handleRejectProposal}
        onRefreshTask={(updated) => {
          if (!updated || !updated.id) return;
          setTasks((prev) => prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)));
        }}
      />
      {pageCount > 1 ? (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {totalCount.toLocaleString()} task{totalCount === 1 ? '' : 's'} • Page {page} of {pageCount}
          </Typography>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            size="small"
          />
        </Stack>
      ) : null}
    </Stack>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Tasks
            </Typography>
            <Typography color="text.secondary">
              Operate manual work and AI-routed inbox tasks from one board.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button startIcon={<SettingsSuggestIcon />} variant="outlined" onClick={openAssignmentDialog}>
              Assignment settings
            </Button>
            <Button startIcon={<ViewColumnIcon />} variant="outlined" onClick={() => setStatusDialogOpen(true)}>
              Add column
            </Button>
            <Button startIcon={<RefreshIcon />} variant="outlined" onClick={handleRefresh}>
              Refresh
            </Button>
            <Button startIcon={<AddIcon />} variant="contained" onClick={openCreateDialog}>
              New task
            </Button>
          </Stack>
        </Stack>

        {summary?.summary ? (
          <Paper variant="outlined" sx={{ p: 2, backgroundColor: (theme) => theme.palette.action.hover }}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <AutoAwesomeIcon color="primary" sx={{ mt: 0.25 }} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.5 }}>
                  Today's triage
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.25 }}>
                  {summary.summary}
                </Typography>
              </Box>
              <Tooltip title={audioPlaying ? 'Pause briefing' : 'Listen to today\'s triage'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={handlePlayAudio}
                    disabled={audioLoading}
                    color={audioPlaying ? 'primary' : 'default'}
                  >
                    {audioLoading
                      ? <CircularProgress size={16} />
                      : audioPlaying
                        ? <PauseIcon fontSize="small" />
                        : <PlayArrowIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Regenerate">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => loadSummary(true)}
                    disabled={summaryLoading}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Paper>
        ) : null}

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {[
            { value: 'all', label: 'All', count: sourceCounts.total },
            { value: 'inbox', label: 'Inbox', count: sourceCounts.inbox },
            { value: 'budget', label: 'Budget', count: sourceCounts.budget },
            { value: 'pacing', label: 'Pacing', count: sourceCounts.pacing },
            { value: 'zip', label: 'ZIP review', count: sourceCounts.zip },
            { value: 'fb-push', label: 'Push to FB', count: sourceCounts['fb-push'] },
            { value: 'manual', label: 'Manual', count: sourceCounts.manual },
          ].map((chip) => {
            const isSelected = (filters.source || 'all') === chip.value;
            const isEmpty = !chip.count;
            return (
              <Chip
                key={chip.value}
                label={`${chip.label}${chip.count != null ? ` · ${chip.count}` : ''}`}
                color={isSelected ? 'primary' : 'default'}
                variant={isSelected ? 'filled' : 'outlined'}
                onClick={() => handleFiltersChange('source', chip.value)}
                sx={{ opacity: isEmpty && !isSelected ? 0.5 : 1 }}
              />
            );
          })}
        </Stack>

        <TaskFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          statusOptions={statusOptions}
          clientOptions={clients}
          assigneeOptions={assignees}
          loading={filtersLoading}
          onReset={handleResetFilters}
        />

        <Box>
          <Tabs value={viewMode} onChange={(_, value) => setViewMode(value)}>
            <Tab label="Table" value="table" />
            <Tab label="Board" value="board" />
          </Tabs>
          <Box sx={{ position: 'relative' }}>
            {loading && <LinearProgress sx={{ position: 'absolute', top: -2, left: 0, right: 0, zIndex: 1 }} />}
          </Box>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        {viewMode === 'table' ? tableContent : boardContent}
      </Stack>

      <TaskDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleCreateOrUpdateTask}
        statusOptions={statusOptions}
        clients={clients}
        assignees={assignees}
        initialValues={editingTask}
      />

      <StatusDialog
        open={statusDialogOpen}
        onClose={handleStatusDialogClose}
        onSubmit={handleAddStatus}
        existingValues={statusColumns.map((status) => status.value)}
      />

      <TaskAssignmentSettingsDialog
        open={assignmentDialogOpen}
        onClose={handleAssignmentDialogClose}
        assignmentUsers={assignmentUsers}
        assignmentRules={assignmentRules}
        clients={clients}
        savingUserId={savingAssignmentUserId}
        savingRuleId={savingAssignmentRuleId}
        deletingRuleId={deletingAssignmentRuleId}
        onSaveUser={handleSaveAssignmentUser}
        onCreateRule={handleCreateAssignmentRule}
        onUpdateRule={handleUpdateAssignmentRule}
        onDeleteRule={handleDeleteAssignmentRule}
      />

      <Snackbar
        open={Boolean(pendingSend)}
        autoHideDuration={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {pendingSend ? (
          <Alert
            severity={pendingSend.secondsLeft > 0 ? 'warning' : 'success'}
            variant="filled"
            sx={{ minWidth: 360, alignItems: 'center' }}
            action={(
              pendingSend.secondsLeft > 0 ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleCancelPendingSend}
                  sx={{ fontWeight: 700 }}
                >
                  Cancel
                </Button>
              ) : null
            )}
          >
            {pendingSend.secondsLeft > 0
              ? `Sending email in ${pendingSend.secondsLeft}s — ${pendingSend.taskTitle}`
              : `Email sent — ${pendingSend.taskTitle}`}
          </Alert>
        ) : null}
      </Snackbar>
    </Box>
  );
}

export default TaskBoardPage;
